import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, type OperationContext } from 'urql';

import { gql } from '@/shared/types/gql';
import { refetchToken } from '@shared/auth/auth';
import { isImpersonating } from '@shared/auth/token.store';

const WORKSPACE_ACCOUNT_FRAGMENT = gql`
  fragment WorkspaceAccount on Account {
    id
    company {
      id
      name
      displayName
      logoUrl
      tenant {
        id
        branding {
          logoUrl
          faviconUrl
        }
      }
    }
  }
`;

export const WORKSPACES_QUERY = gql`
  query Workspaces {
    me {
      id
      account {
        ...WorkspaceAccount
      }
      allowedAccounts {
        ...WorkspaceAccount
      }
    }
  }
  ${WORKSPACE_ACCOUNT_FRAGMENT}
`;

export const WORKSPACES_REFRESH_QUERY = gql`
  query WorkspacesRefresh {
    me {
      id
      account {
        ...WorkspaceAccount
      }
      allowedAccounts {
        ...WorkspaceAccount
      }
    }
  }
  ${WORKSPACE_ACCOUNT_FRAGMENT}
`;

export const SWITCH_ACCOUNT = gql`
  mutation SwitchAccount($accountId: String!) {
    me {
      switchAccount(accountId: $accountId)
    }
  }
`;

interface TenantNode {
  id: string;
  branding?: { logoUrl?: string | null; faviconUrl?: string | null } | null;
}

interface CompanyNode {
  id: string;
  name?: string | null;
  displayName?: string | null;
  logoUrl?: string | null;
  tenant?: TenantNode | null;
}

interface AccountNode {
  id: string;
  company?: CompanyNode | null;
}

export interface WorkspacesData {
  me?: {
    id: string;
    account?: AccountNode | null;
    allowedAccounts?: AccountNode[] | null;
  } | null;
}

export interface Workspace {
  accountId: string;
  companyId: string | null;
  name: string | null;
  logoUrl: string | null;
  iconUrl: string | null;
}

function nonEmpty(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function workspaceLogo(company: CompanyNode | null | undefined): string | null {
  return nonEmpty(company?.tenant?.branding?.logoUrl) ?? nonEmpty(company?.logoUrl);
}

export function workspaceIcon(company: CompanyNode | null | undefined): string | null {
  return nonEmpty(company?.tenant?.branding?.faviconUrl) ?? workspaceLogo(company);
}

export function activeFavicon(data: WorkspacesData | undefined): string | null {
  return nonEmpty(data?.me?.account?.company?.tenant?.branding?.faviconUrl);
}

function toWorkspace(account: AccountNode): Workspace {
  const company = account.company ?? null;
  return {
    accountId: account.id,
    companyId: company?.id ?? null,
    name: company?.displayName ?? company?.name ?? null,
    logoUrl: workspaceLogo(company),
    iconUrl: workspaceIcon(company),
  };
}

export function collectWorkspaces(data: WorkspacesData | undefined): {
  active: Workspace | null;
  workspaces: Workspace[];
} {
  const me = data?.me;
  const activeAccount = me?.account ?? null;
  const byAccountId = new Map<string, Workspace>();

  for (const account of [activeAccount, ...(me?.allowedAccounts ?? [])]) {
    if (!account?.id || byAccountId.has(account.id)) continue;
    byAccountId.set(account.id, toWorkspace(account));
  }

  return {
    active: activeAccount?.id ? (byAccountId.get(activeAccount.id) ?? null) : null,
    workspaces: [...byAccountId.values()],
  };
}

export function useActiveWorkspace(): Workspace | null {
  const [{ data }] = useQuery<WorkspacesData>({
    query: WORKSPACES_QUERY,
    requestPolicy: 'cache-first',
  });
  return useMemo(() => collectWorkspaces(data).active, [data]);
}

export interface WorkspacesRefetchClient {
  query(
    doc: typeof WORKSPACES_REFRESH_QUERY,
    vars: Record<string, never>,
    context?: Partial<OperationContext>,
  ): { toPromise(): Promise<unknown> };
}

export async function refreshWorkspaces(client: WorkspacesRefetchClient): Promise<void> {
  try {
    await client.query(WORKSPACES_REFRESH_QUERY, {}, { requestPolicy: 'network-only' }).toPromise();
  } catch {
    return;
  }
}

const NOT_ALLOWED = /not allowed|forbidden|403/i;

export function isSwitchRejected(message: string | undefined): boolean {
  return !!message && NOT_ALLOWED.test(message);
}

export interface WorkspacesState {
  active: Workspace | null;
  workspaces: Workspace[];
  canSwitch: boolean;
  fetching: boolean;
  switching: string | null;
  switchTo: (accountId: string, destination?: string) => Promise<boolean>;
}

export function useWorkspaces(): WorkspacesState {
  const [{ data, fetching }] = useQuery<WorkspacesData>({
    query: WORKSPACES_QUERY,
    requestPolicy: 'cache-first',
  });
  const [, runSwitch] = useMutation(SWITCH_ACCOUNT);
  const [switching, setSwitching] = useState<string | null>(null);

  const { active, workspaces } = useMemo(() => collectWorkspaces(data), [data]);

  const switchTo = useCallback(
    async (accountId: string, destination = '/'): Promise<boolean> => {
      if (isImpersonating()) return false;
      setSwitching(accountId);
      try {
        const result = await runSwitch({ accountId });
        if (isSwitchRejected(result.error?.message)) return false;

        await refetchToken();
        window.location.assign(destination);
        return true;
      } catch {
        return false;
      } finally {
        setSwitching(null);
      }
    },
    [runSwitch],
  );

  return {
    active,
    workspaces,
    canSwitch: workspaces.length > 1 && !isImpersonating(),
    fetching,
    switching,
    switchTo,
  };
}
